import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Users,
  ExternalLink,
  Share2,
  Loader2,
  Plus,
  TrendingUp,
  Copy,
  Check,
  Edit2,
  Target,
} from 'lucide-react';

const MyTeamPagesSection = () => {
  const { token } = useAuth();
  const [teamPages, setTeamPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchMyTeamPages();
  }, []);

  const fetchMyTeamPages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/team-pages/my-pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeamPages(data.team_pages || []);
      }
    } catch (err) {
      console.error('Failed to load team pages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (slug, id) => {
    const url = `${window.location.origin}/fundraise/${slug}`;
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-team-pages-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Users className="w-5 h-5 text-violet-400" />
            My Fundraising Pages
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Track and manage your team fundraising pages
          </p>
        </div>
        <Link to="/discover">
          <Button variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" /> Start New
          </Button>
        </Link>
      </div>

      {/* No Pages */}
      {teamPages.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <div className="w-16 h-16 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-violet-400" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Fundraising Pages Yet</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Start your own fundraising page to support a campaign and share it with your network.
          </p>
          <Link to="/discover">
            <Button className="bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700">
              <Target className="w-4 h-4 mr-2" /> Find a Campaign
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {teamPages.map((page) => {
            const progress = page.goal_amount
              ? Math.min((page.amount_raised / page.goal_amount) * 100, 100)
              : 0;

            return (
              <div
                key={page.id}
                className="bg-card rounded-xl p-5 border border-border hover:border-emerald-500/30 transition-colors"
              >
                {/* Header */}
                <div className="flex items-start gap-3 mb-4">
                  <img
                    src={page.cover_image_url || page.parent_campaign?.image || 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=200'}
                    alt={page.title}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <Badge
                      className={`text-xs mb-1 ${
                        page.status === 'active'
                          ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                          : 'bg-red-500/20 text-red-400 border-red-500/30'
                      }`}
                    >
                      {page.status}
                    </Badge>
                    <Link to={`/fundraise/${page.slug}`}>
                      <h3 className="font-semibold text-foreground hover:text-emerald-400 line-clamp-1">
                        {page.title}
                      </h3>
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      for {page.parent_campaign?.title || 'Campaign'}
                    </p>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-semibold text-emerald-400">
                      {formatCurrency(page.amount_raised)}
                    </span>
                    {page.goal_amount > 0 && (
                      <span className="text-muted-foreground">
                        of {formatCurrency(page.goal_amount)}
                      </span>
                    )}
                  </div>
                  {page.goal_amount > 0 && (
                    <Progress value={progress} className="h-2 bg-secondary" />
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <span>{page.donor_count || 0} donors</span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-emerald-400" />
                    {Math.round(progress)}% of goal
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Link to={`/fundraise/${page.slug}`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full">
                      <ExternalLink className="w-4 h-4 mr-2" /> View Page
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyLink(page.slug, page.id)}
                  >
                    {copiedId === page.id ? (
                      <Check className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                  <Link to={`/team-page/${page.id}/edit`}>
                    <Button variant="outline" size="sm">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyTeamPagesSection;
